import {
  animate,
  query as q,
  state,
  style,
  transition,
  trigger,
  AnimationMetadata,
  AnimationQueryOptions,
  stagger,
  group,
  animateChild,
} from '@angular/animations';

const query = (
  s: string,
  a: AnimationMetadata | AnimationMetadata[],
  o: AnimationQueryOptions | null | undefined = { optional: true },
) => q(s, a, o);

export const slideToTop = trigger('slideToTop', [
  transition(':increment, :decrement', [
    query(':enter', style({ transform: 'translateY(100%)', opacity: 0 })),
    query(':enter', stagger(100, [animate('.2s ease-out', style({ transform: 'translateY(0)', opacity: 1 }))])),
  ]),
]);

export const slideToTopVoid = trigger('slideToTopVoid', [
  transition(':enter, void => *', [
    query(':enter', style({ transform: 'translateY(100%)', opacity: 0 })),
    query(':enter', stagger(100, [animate('.2s ease-out', style({ transform: 'translateY(0)', opacity: 1 }))])),
  ]),
]);

export const transformPanel = trigger('transformPanel', [
  state('void', style({ opacity: 0, transform: 'scale(1, 0.6)' })),
  transition(
    'void => showing',
    animate('200ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1, 1)' })),
  ),
  transition('* => void', animate('150ms ease-in', style({ opacity: 0 }))),
]);

export const rotate = trigger('rotate', [
  state('true', style({ transform: 'rotate(180deg)' })),
  state('false', style({ transform: 'rotate(0)' })),
  transition('* => *', animate('0.125s ease-in-out')),
]);

export const refresh = trigger('refresh', [
  state('refresh', style({ transform: 'rotate({{count}}turn)' }), { params: { count: 1 } }),
  state('loop', style({ transform: 'rotate({{count}}turn)' }), { params: { count: 1 } }),
  state('done', style({ transform: 'rotate(0)' })),
  transition('done => refresh', animate('0.6s ease-in-out')),
  transition('refresh => loop', animate('0.6s ease-in-out')),
  transition('loop => refresh', animate('0.6s ease-in-out')),
  transition('refresh => done', animate('0.25s ease-in')),
  transition('loop => done', animate('0.25s ease-in')),
]);

export const dialogContainer = trigger('dialogContainer', [
  state(
    'void, exit',
    style({
      opacity: 0,
      overflow: 'hidden',
      transform: 'scale(0.7)',
    }),
  ),
  state('enter', style({ transform: 'none', overflow: 'hidden' })),
  transition(
    '* => enter',
    group([
      animate('200ms cubic-bezier(0, 0, 0.2, 1)', style({ transform: 'none', opacity: 1 })),
      query('@*', animateChild()),
    ]),
  ),
  transition(
    '* => void, * => exit',
    animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0, transform: 'scale(0.8)' })),
  ),
]);

export const sidebar = trigger('sidebar', [
  state('void', style({ transform: 'translateX(100%)' })),
  transition('void => showing', animate('250ms ease-out', style({ transform: 'translateX(0)' }))),
  transition('* => void', animate('175ms ease-in', style({ transform: 'translateX(100%)' }))),
]);

export const explode = trigger('explode', [
  state('void', style({ transform: 'scale(0.5)', opacity: 0 })),
  transition('void => show', animate('150ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))),
  transition('* => void', animate('150ms ease-in', style({ transform: 'scale(0.5)', opacity: 0 }))),
]);

export const slideLabel = trigger('slideLabel', [
  state('void', style({ transform: 'translateY(-50%)', opacity: 0 })),
  transition('void => show', animate('200ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))),
  transition('* => void', animate('150ms ease-in', style({ transform: 'translateY(-50%)', opacity: 0 }))),
]);

export const loadMapEnter = trigger('loadMapEnter', [
  transition(':enter', [
    query('.status-area', style({ transform: 'translateX(85%)', opacity: 0 })),
    query('.status, .time-range', style({ transform: 'scale(0.2)', opacity: 0 })),
    query('.map-area', style({ opacity: 0 })),
    query('.status-area', animate('.25s ease-out', style({ transform: 'translateX(0)', opacity: 1 }))),
    group([
      query(
        '.status, .time-range',
        stagger(100, animate('.15s ease-out', style({ transform: 'scale(1)', opacity: 1 }))),
      ),
      query('.map-area', animate('.35s ease-out', style({ opacity: 1 }))),
    ]),
  ]),
]);

export const expandPanel = trigger('expandPanel', [
  state('close', style({ height: 0 })),
  transition('close => open', animate('.25s ease-out', style({ height: '{{x}}px' })), { params: { x: 0 } }),
  transition('open => close', animate('.15s ease-in', style({ height: 0 }))),
]);

export const slideComments = trigger('slideComments', [
  transition(':increment', [
    query(':enter', style({ transform: 'translateY(100%)', opacity: 0 })),
    query(':enter', stagger(100, [animate('.2s ease-out', style({ transform: 'translateY(0)', opacity: 1 }))])),
  ]),
  transition(':decrement', [
    query(':leave', style({ transform: 'translateX(0)', opacity: 1 })),
    query(':leave', stagger(100, animate('.2s ease-in', style({ transform: 'translateX(100%)', opacity: 0 })))),
  ]),
]);

export const showChangeStatusCtrl = trigger('showChangeStatusCtrl', [
  transition('false => true', [
    query(':enter', style({ transform: 'scale(0)', opacity: 0 })),
    query(':enter', stagger(100, animate('.15s ease-out', style({ transform: 'scale(1)', opacity: 1 })))),
  ]),
  transition('true => false', [
    query(':leave', style({ transform: 'scale(1)', opacity: 1 })),
    query(':leave', stagger(100, animate('.15s ease-out', style({ transform: 'scale(0)', opacity: 0 })))),
  ]),
]);

export const transformMenu = trigger('transformMenu', [
  state('void', style({ opacity: 0, transform: 'scale(0.8)' })),
  transition(
    'void => enter',
    animate('120ms cubic-bezier(0, 0, 0.2, 1)', style({ opacity: 1, transform: 'scale(1)' })),
  ),
  transition('* => void', animate('100ms 25ms linear', style({ opacity: 0 }))),
]);

export const selfCollectorEnter = trigger('selfCollectorEnter', [
  transition(':enter', [
    query('.header, .body', style({ transform: 'translateX(50%)', opacity: 0 })),
    query('.header, .body', stagger(75, animate('0.25s ease-out', style({ transform: 'translateX(0)', opacity: 1 })))),
  ]),
]);

export const popover = trigger('popover', [
  state('void', style({ opacity: 0, transform: 'scale(0.7)' })),
  transition('void => enter', animate('150ms ease-out', style({ opacity: 1, transform: 'scale(1)' }))),
  transition('* => void', animate('100ms ease-in', style({ opacity: 0, transform: 'scale(0.85)' }))),
]);

export const slideToBottom = trigger('slideToBottom', [
  transition(':increment, :decrement', [
    query(':enter', style({ transform: 'translateY(-100%)', opacity: 0 })),
    query(':enter', stagger(100, [animate('.2s ease-out', style({ transform: 'translateY(0)', opacity: 1 }))])),
  ]),
]);

export const scaleBar = trigger('scaleBar', [
  transition('void => enter', [
    query('.bar', style({ transform: 'scaleX(0)', 'transform-origin': '0 50%' })),
    query(
      '.bar',
      stagger(250, animate('.25s ease-out', style({ transform: 'scaleX(1)', 'transform-origin': '0 50%' }))),
    ),
  ]),
]);
